<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch, provide } from 'vue';

import { IonApp } from '@ionic/vue';

import { updateHttpFhirService } from '@/common/api/httpFhir.service.js';
import { SplashScreen } from '@capacitor/splash-screen';
import { EventSourcePolyfill } from 'event-source-polyfill';
import { useBnNoAutocomplete } from '@/composables/useBnNoAutocomplete.js';
import { debounce } from 'lodash';
import { useRoute } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { useLockout } from '@/composables/useLockout';
import { useLogoutOnLastTabClose } from '@/composables/useLogoutOnLastTabClose';
import { useNotificationStore } from '@/stores/notification';
import { useMousePressed, onKeyStroke } from '@vueuse/core';
import { registerAndroidNativeBackbtn, removeBackBtnListiner } from '@/common/nativeBackButton';
// import useMercure from '@/composables/useMercure.js';

const route = useRoute();
const { resetLockoutActivity, lockOutCountDown, resumeLockout, pauseLockout, stopTokenRefreshTimer } = useLockout();
useLogoutOnLastTabClose();
const { pressed } = useMousePressed();

const rootEl = ref(null);
const globalEventSource = ref(null);

provide('lockoutControls', {
    pauseLockout,
    resumeLockout,
    stopTokenRefreshTimer,
});

const debouncedResetLockoutActivity = debounce(() => resetLockoutActivity(), 1000, { maxWait: 2000 });

watch(pressed, debouncedResetLockoutActivity);

onKeyStroke(true, debouncedResetLockoutActivity);

const authStore = useAuthStore();
const notificationStore = useNotificationStore();
const { repairAutocomplete } = useBnNoAutocomplete();

const appSnackBarHandler = () => {
    notificationStore.shift();
};

// const handleMessage = (messageData) => {
//     notificationStore.add(messageData.id);
// };

const callWatchers = () => {
    // this.getSubscriptions(); // in mercure-mixin
    repairAutocomplete(rootEl.value.$el); // in bn-no-autocomplete-mixin
};

const handleMercureConnection = () => {
    // if no token, account or user, do nothing.  Also close eventSource if exists.
    if (!authStore.token || !authStore.account?.id || !authStore.user?.apiUser?.id) {
        if (globalEventSource.value instanceof EventSourcePolyfill) {
            globalEventSource.value.close();
        }

        return;
    }

    // if a new token is issued we want to close the old EventSource object to make way for new one.
    if (globalEventSource.value instanceof EventSourcePolyfill) {
        globalEventSource.value.close();
    }

    const url = new URL('https://bestnotesfresh.com:1337/.well-known/mercure');
    url.searchParams.append('topic', 'global');
    // Subscribe to updates of several Book resources
    url.searchParams.append('topic', 'account/' + authStore.account?.id + '/user/' + authStore.user.apiUser?.id);
    // All Review resources will match this pattern
    url.searchParams.append('topic', 'account/' + authStore.account?.id);

    // 1361-Commented Out
    // this.globalEventSource = new EventSourcePolyfill(url, {
    //     headers: {
    //         Authorization: 'Bearer ' + this.token,
    //     },
    // });
    //
    // this.globalEventSource.onmessage = (event) => {
    //     this.handleMessage(JSON.parse(event.data));
    // };
};

const currentNotification = computed(() => {
    return notificationStore.notifications?.length ? notificationStore.notifications[0] : false;
});

const useIonContainer = computed(() => {
    return !route?.path?.includes('/print');
});

let observer;

onMounted(async () => {
    updateHttpFhirService();
    if (SplashScreen) {
        await SplashScreen?.hide();
    }
    removeBackBtnListiner();
    registerAndroidNativeBackbtn();
    handleMercureConnection();

    let triggerMutations = debounce(callWatchers, 200);
    observer = new MutationObserver(triggerMutations);

    observer.observe(rootEl.value.$el, { childList: true, subtree: true });
});

onBeforeUnmount(() => {
    if (globalEventSource.value instanceof EventSourcePolyfill) {
        globalEventSource.value.close();
    }
    if (observer instanceof MutationObserver) {
        observer.disconnect();
    }
});

watch(
    () => authStore.fhirApiUri,
    () => {
        updateHttpFhirService();
    },
);
</script>

<template>
    <IonApp v-if="useIonContainer" ref="rootEl">
        <v-app :style="$vuetify.display.mdAndDown ? 'top: 12px;' : 'top: 0px;'">
            <v-theme-provider theme="lightTheme">
                <v-snackbar
                    id="appSnackBar"
                    :color="currentNotification.color || 'info'"
                    :model-value="notificationStore.notifications.length > 0"
                    location="top"
                    multi-line
                    :timeout="currentNotification.timeout || 3000"
                    data-cy="noAccessWarning"
                    @update:model-value="appSnackBarHandler"
                >
                    {{ currentNotification.message }}

                    <template #actions>
                        <v-btn variant="outlined" color="white" data-cy="appSnackBarCloseBtn" @click="notificationStore.shift()">
                            <template v-if="notificationStore.notifications.length === 1"> Close </template>
                            <template v-else> Next ({{ notificationStore.notifications.length - 1 }} more) </template>
                        </v-btn>
                    </template>
                </v-snackbar>

                <!-- Lockout Snackbar -->
                <v-snackbar :model-value="!!lockOutCountDown" color="error" top timeout="-1">
                    <div class="d-flex">
                        Screen will lock in {{ lockOutCountDown }} seconds
                        <!--                <v-btn text color="white" @click="lockScreen">Lock</v-btn>-->
                        <v-btn variant="outlined" color="white" class="ml-4" @click="resetLockoutActivity">Stay logged in</v-btn>
                    </div>
                </v-snackbar>

                <div>
                    <router-view />
                </div>
            </v-theme-provider>
        </v-app>
    </IonApp>

    <v-app v-else ref="rootEl">
        <v-snackbar
            id="appSnackBar"
            :color="currentNotification.color || 'info'"
            :model-value="notificationStore.notifications.length > 0"
            location="top"
            multi-line
            :timeout="currentNotification.timeout || 3000"
            @update:model-value="appSnackBarHandler"
        >
            {{ currentNotification.message }}

            <template #actions>
                <v-btn variant="outlined" color="white" data-cy="appSnackBarCloseBtn" @click="notificationStore.shift()">
                    <template v-if="notificationStore.notifications.length === 1"> Close </template>
                    <template v-else> Next ({{ notificationStore.notifications.length - 1 }} more) </template>
                </v-btn>
            </template>
        </v-snackbar>
        <!-- Lockout Snackbar -->
        <v-snackbar :model-value="!!lockOutCountDown" color="error" top timeout="-1">
            <div class="d-flex">
                Screen will lock in {{ lockOutCountDown }} seconds
                <!-- <v-btn text color="white" @click="lockScreen">Lock</v-btn>-->
                <v-btn variant="outlined" color="white" class="ml-4" @click="resetLockoutActivity">Stay logged in</v-btn>
            </div>
        </v-snackbar>

        <router-view />
    </v-app>
</template>

<style>
/* Fix the snackbar margin to account for the notch */

.v-snack,
.v-snackbar__wrapper {
    margin-top: var(--ion-safe-area-top, 0) !important;
}
</style>
